package dev.lingvo.site.pages.app.reports

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.Role
import dev.lingvo.shared.SpendGrouping
import dev.lingvo.shared.SpendReportDto
import dev.lingvo.shared.SpendRowDto
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LChartLineIcon
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableAlign
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.util.csvRow
import dev.lingvo.site.util.downloadText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.ChartPoint
import dev.lingvo.site.ds.LBarChart
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSectionTitle

/**
 * Where the credits went.
 *
 * **Gross, refunded and net as three columns.** `releaseCredits` decrements the counter it
 * incremented, so a month with 900 spent and 400 refunded is indistinguishable from one with 500
 * spent if you only show a total — and refunds happen on every failed provider call and every reaped
 * job, which makes that the ordinary case.
 *
 * Rows paid for by the workspace's own provider key show the work and **no cost column at all**,
 * rather than a zero. A zero in a money column reads as "free to us"; this is "not ours to count".
 */
@Page("/app/reports/spend")
@Layout(".components.layouts.AppLayout")
@Composable
fun SpendReportPage() {
    val ctx = rememberPageContext()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    var grouping by remember { mutableStateOf(SpendGrouping.PROJECT) }

    // A retry that retries. `ErrorState` draws its button whenever `onRetry` is non-null, so
    // `{ }` produced a visible control that does nothing — on a page reached precisely when
    // the network is unreliable, whose only other exit is a full browser reload.
    var refreshKey by remember { mutableStateOf(0) }

    // Hoisted above the column so the header can state how many rows a download would carry — but
    // still gated, so the refused request is never made. Spend is owners and admins at the server:
    // `SpendRoutes` refuses everyone else, and a load that goes out anyway comes back 403 after a
    // wait, on a page that could have answered before it asked.
    val canReadSpend = workspace == null || workspace.role.atLeast(Role.ADMIN)
    val state = loadAsync(slug, grouping, canReadSpend, refreshKey) {
        if (!canReadSpend) null else slug?.let { ApiClient.spendReport(it, grouping) }
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        val loadedReport = (state as? Async.Success)?.value
        PageHeader(
            t("app.reports.spend.spend", "Spend"),
            tf(
                "app.reports.spend.machine_translation_credits_by_grouping",
                "Machine-translation credits, by {grouping}.",
                "grouping" to grouping.labelInSentence(),
            ),
            mark = { LChartLineIcon() },
            actions = {
                // The real count and the honest scope: this writes the rows under the grouping
                // currently in force, over the window named on the page, and says so beside it.
                loadedReport?.rows?.takeIf { it.isNotEmpty() }?.let { rows ->
                    LButton(
                        tPlural(
                            "app.reports.spend.count.download_row",
                            rows.size,
                            "Download {count} row",
                            "Download {count} rows",
                        ),
                        {
                            downloadText(
                                spendCsv(loadedReport),
                                "lingvo-spend-${grouping.name.lowercase()}.csv",
                                "text/csv;charset=utf-8",
                            )
                        },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LDownloadIcon() },
                    )
                }
            },
        )

        // The refusal, before anything else on the page. Without it the reader met the default
        // subject — "You don't have access to this page" — after a wait, on a page every sibling
        // answers before it loads anything.
        if (!canReadSpend) {
            LEmptyState(
                t("app.reports.spend.only_owners_and_admins_can_see", "Only owners and admins can see spend"),
                t("app.reports.spend.ask_an_owner_or_an_admin", "Ask an owner or an admin if you need the numbers."),
                Modifier.fillMaxWidth().maxWidth(640.px),
                LEmptyStateIcon.FORBIDDEN,
            ) {}
            return@Column
        }

        // `LSegmented`, not a row of buttons whose selection is carried by variant alone. This is
        // one choice among three, and drawn as buttons it announced three unrelated actions with
        // nothing saying which was in force — the selected one differed only by colour. The
        // component renders a radio group with roving focus and arrow keys, which is what a
        // segmented control is for.
        LSegmented(
            options = SpendGrouping.entries.map { option ->
                LSegmentedOption(option, option.label())
            },
            selected = grouping,
            onSelected = { grouping = it },
            label = t("app.reports.spend.group_spend_by", "Group spend by"),
        )

        LAsyncContent(
            state = state,
            rows = 4,
            loadingLabel = t("app.reports.spend.loading_spend", "Loading spend"),
            subject = t("app.reports.spend.spend_2", "spend"),
            onRetry = { refreshKey++ },
        ) { report ->
            when {
                // Not "choose a workspace": `WorkspaceSelection` falls back to the strongest
                // membership, so null here means this account is in none, and the picker it
                // sent the reader to is empty. `/app` answers this state with the one action
                // that fixes it.
                report == null -> LEmptyState(
                    t("app.reports.spend.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                    t("app.reports.spend.a_workspace_holds_the_projects_this", "A workspace holds the projects this report is about. Create one, or ask " +
                        "whoever invited you to send the invitation again."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.INSIGHTS,
                ) {
                    LButton(
                        t("app.reports.spend.create_a_workspace", "Create a workspace"),
                        { ctx.router.tryRoutingTo("/app/workspaces") },
                    )
                }
                report.rows.isEmpty() -> LEmptyState(
                    t("app.reports.spend.no_machine_translation_yet", "No machine translation yet"),
                    t("app.reports.spend.credits_are_only_spent_by_pre_2", "Credits are only spent by pre-translate and the translate button. Your own " +
                        "provider key costs none at all."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.INSIGHTS,
                ) {}
                else -> {
                    // The window, in UTC, on the page. `credit_usage.period` is a UTC month, and
                    // an admin in UTC+9 reading "October" otherwise gets numbers matching neither
                    // their experience nor their invoice.
                    LMuted(tf(
                        "app.reports.spend.window_range",
                        "{from} to {to} UTC",
                        "from" to report.fromUtc.take(19).replace('T', ' '),
                        "to" to report.toUtc.take(19).replace('T', ' '),
                    ))
                    report.truncatedBefore?.let { boundary ->
                        LAlert(
                            t("app.reports.spend.part_of_this_window_predates_the", "Part of this window predates the ledger"),
                            tf(
                                "app.reports.spend.credits_before_date_were_counted_per_month",
                                "Credits before {date} were counted per month and cannot be broken " +
                                    "down. The rows below cover everything after it.",
                                "date" to boundary.take(10),
                            ),
                            variant = LAlertVariant.INFO,
                        )
                    }
                    SpendChart(report.rows, grouping)
                    SpendTable(report.rows)
                    // Grouped, for the same reason the credits ledger groups: a five-digit
                    // total is misread without it.
                    LMuted(tPlural(
                        "app.reports.spend.net_across_window",
                        report.creditsNet,
                        "Net across this window: {count} credit.",
                        "Net across this window: {count} credits.",
                    ))
                }
            }
        }
    }
}

/**
 * The same rows as a picture, above the same rows as a table.
 *
 * This page had only the table, and a table answers "what did each one cost" while hiding the
 * question an admin actually opens this page with — *which one is the expensive one*. Ten rows of
 * five-digit numbers do not rank themselves; bars do it before the numbers are read.
 *
 * **Net, not gross.** The bar is `creditsNet`, the same figure the table's own total uses, because
 * a chart and a table on one screen disagreeing about a number is worse than having neither.
 *
 * Own-key rows are excluded rather than drawn at zero. A zero-length bar in a cost comparison reads
 * as "this was free", and own-key work is not free — it is not ours to count, which is why the
 * table gives it no cost column either.
 */
@Composable
private fun SpendChart(rows: List<SpendRowDto>, grouping: SpendGrouping) {
    val points = rows
        .filterNot { it.byok }
        .filter { it.creditsNet > 0 }
        .sortedByDescending { it.creditsNet }
        .take(SPEND_CHART_ROWS)
        .map { row -> ChartPoint(spendRowLabel(row, grouping), row.creditsNet.toDouble()) }
    if (points.isEmpty()) return

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(
                when (grouping) {
                    SpendGrouping.PROJECT -> t("app.reports.spend.chart_by_project", "Where the credits went")
                    SpendGrouping.LOCALE -> t("app.reports.spend.chart_by_locale", "Which languages cost most")
                    SpendGrouping.PROVIDER -> t("app.reports.spend.chart_by_provider", "Which providers cost most")
                },
            )
            LBarChart(
                points = points,
                caption = t("app.reports.spend.chart_caption", "Credits spent, largest first"),
                emptyMessage = t("app.reports.spend.chart_empty", "No metered credits in this window."),
            )
            if (rows.count { !it.byok && it.creditsNet > 0 } > SPEND_CHART_ROWS) {
                // Named rather than silently truncated: a chart that shows ten of forty rows and
                // says so is a summary, and one that does not is a wrong answer.
                LMuted(
                    tf(
                        "app.reports.spend.chart_truncated",
                        "Top {count} shown. The table below has every row.",
                        "count" to SPEND_CHART_ROWS,
                    ),
                )
            }
        }
    }
}

/** What a row is called, in whichever way the report is grouped. */
/**
 * The three words the grouping control shows, looked up rather than derived from the enum.
 *
 * `option.name.lowercase().replaceFirstChar(…)` is a wire constant with a capital letter on it: the
 * radio group announced "Project / Locale / Provider" in English on every locale, and the page
 * subtitle spliced the same constant into a translated sentence — "Maschinelle
 * Übersetzungs-Credits, nach provider." `tr`, not `t`: the segment labels are built in a `map`
 * lambda, which is not a composable scope.
 */
internal fun SpendGrouping.label(): String = when (this) {
    SpendGrouping.PROJECT -> tr("app.reports.spend.grouping_project", "Project")
    SpendGrouping.LOCALE -> tr("app.reports.spend.grouping_locale", "Locale")
    SpendGrouping.PROVIDER -> tr("app.reports.spend.grouping_provider", "Provider")
}

/**
 * The same word where the sentence has already started — "Machine-translation credits, by project."
 *
 * A separate key rather than `label().lowercase()`, which is wrong in German (every noun is
 * capitalised) and in Turkish (`I` lowercases to `ı`), exactly as `Role.labelInSentence()` is.
 */
internal fun SpendGrouping.labelInSentence(): String = when (this) {
    SpendGrouping.PROJECT -> tr("app.reports.spend.grouping_sentence_project", "project")
    SpendGrouping.LOCALE -> tr("app.reports.spend.grouping_sentence_locale", "locale")
    SpendGrouping.PROVIDER -> tr("app.reports.spend.grouping_sentence_provider", "provider")
}

private fun spendRowLabel(row: SpendRowDto, grouping: SpendGrouping): String = when (grouping) {
    SpendGrouping.PROJECT -> row.projectName
        ?: row.projectSlug
        ?: tr("app.reports.spend.chart_deleted_project", "Deleted project")
    SpendGrouping.LOCALE -> row.locale?.let { localeInfo(it).englishName }
        ?: tr("app.reports.spend.chart_no_locale", "No language")
    SpendGrouping.PROVIDER -> row.provider
        ?: tr("app.reports.spend.chart_no_provider", "No provider")
}

/** Enough to rank, few enough to read at a glance. The table carries the rest. */
private const val SPEND_CHART_ROWS = 10

@Composable
private fun SpendTable(rows: List<SpendRowDto>) {
    LTable(caption = t("app.reports.spend.credit_spend", "Credit spend")) {
        LTableHead {
            LTableRow {
                // Headers declared per column rather than mapped over one flat list. The flat
                // list was why the figures below ended up right-aligned while their headers stayed
                // left: a single loop cannot say that five of six columns are numbers. It was also
                // why five of these six words were never offered to a translator.
                LTableHeaderCell { Text(t("app.reports.spend.column_where", "Where")) }
                listOf(
                    t("app.reports.spend.column_strings", "Strings"),
                    t("app.reports.spend.column_characters", "Characters"),
                    t("app.reports.spend.column_gross", "Gross"),
                    t("app.reports.spend.column_refunded", "Refunded"),
                    t("app.reports.spend.column_net", "Net"),
                ).forEach { LTableHeaderCell(align = LTableAlign.END) { Text(it) } }
            }
        }
        LTableBody {
            rows.forEach { row ->
                LTableRow {
                    LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                        Text(
                            listOfNotNull(row.projectName, row.locale, row.provider)
                                .joinToString(" · ")
                                .ifBlank { "Workspace" },
                        )
                        if (row.byok) LBadge(t("app.reports.spend.your_key", "your key"))
                    }
                    Number(row.strings)
                    Number(row.sourceChars)
                    if (row.byok) {
                        // No money column, rather than three zeroes. This work cost us nothing to
                        // count and them nothing through us.
                        repeat(3) { LTableCell(align = LTableAlign.END) { Text("—") } }
                    } else {
                        Number(row.creditsGross)
                        Number(row.creditsRefunded)
                        Number(row.creditsNet)
                    }
                }
            }
        }
    }
}

@Composable
private fun Number(value: Int) {
    // A column of numbers is read by comparing them, which proportional digits make harder — and
    // grouped, because five-digit credit counts are misread without it. The same helper on the
    // usage ledger already said so.
    LTableCell(align = LTableAlign.END) { Text(formatCount(value)) }
}

/**
 * The report, as a spreadsheet, with the UTC window in the first line.
 *
 * The window is a header comment rather than a column: repeating the same two timestamps on every
 * row is noise, and leaving them out entirely is how a spend export ends up in a budget meeting
 * with nobody able to say which month it covers. A BYOK row writes no credit figure at all — a zero
 * in a money column reads as "free to us", and this is "not ours to count".
 */
internal fun spendCsv(report: SpendReportDto): String = buildString {
    append(csvRow(listOf(tr("app.reports.spend.csv_window_utc", "Window (UTC)"), report.fromUtc, report.toUtc)))
    append(
        csvRow(
            listOf(
                tr("app.reports.spend.csv_project", "Project"),
                tr("app.reports.spend.csv_locale", "Locale"),
                tr("app.reports.spend.csv_provider", "Provider"),
                tr("app.reports.spend.csv_strings", "Strings"),
                tr("app.reports.spend.csv_source_characters", "Source characters"),
                tr("app.reports.spend.csv_credits_net", "Credits (net)"),
                tr("app.reports.spend.csv_own_key", "Own key"),
            ),
        ),
    )
    report.rows.forEach { row ->
        append(
            csvRow(
                listOf(
                    row.projectName ?: row.projectSlug.orEmpty(),
                    row.locale.orEmpty(),
                    row.provider.orEmpty(),
                    row.strings.toString(),
                    row.sourceChars.toString(),
                    if (row.byok) "" else row.creditsNet.toString(),
                    if (row.byok) tr("app.reports.spend.csv_yes", "Yes") else "",
                ),
            ),
        )
    }
}
